// Client-side wrapper for our Netlify Functions. Adds caching + poster URL helpers.
const FN = '/.netlify/functions';

// Browser session cache so navigating back and forth doesn't refetch.
const memCache = new Map();
const TTL_MS = 1000 * 60 * 10;

async function get(name, params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const url = `${FN}/${name}${qs ? `?${qs}` : ''}`;

  const cached = memCache.get(url);
  if (cached && Date.now() - cached.at < TTL_MS) return cached.value;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`API ${name} failed: ${res.status}`);
  const value = await res.json();
  memCache.set(url, { at: Date.now(), value });
  return value;
}

export const tmdb = {
  nowPlaying:       ({ page = 1, media = 'movie' } = {}) => get('now-playing', { page, media }),
  discover:         (params) => get('discover', params),
  // media: 'movie' (default) or 'tv'
  movie:            (id, media = 'movie') => get('movie', { id, media }),
  media:            (id, media = 'movie') => get('movie', { id, media }),
  genres:           () => get('genres'),
  // loves / ejects: arrays of { tmdb_id, media_type } objects from likes.js
  // seedId (optional): focus on a single positive seed e.g. "movie:299534"
  //   for "Because you loved X" per-seed shelves.
  recommendations:  ({ loves, ejects, items, providers, seedId } = {}) => get('recommendations', {
    loves:  loves  ? loves.map((i)  => `${i.media_type || 'movie'}:${i.tmdb_id}`).join(',') : undefined,
    ejects: ejects ? ejects.map((i) => `${i.media_type || 'movie'}:${i.tmdb_id}`).join(',') : undefined,
    items:  items  ? items.map((i)  => `${i.media_type || 'movie'}:${i.tmdb_id}`).join(',') : undefined,
    providers,
    seedId,
  }),
  search:           ({ q, providers }) => get('search', { q, providers }),
};

// Poster helpers — TMDB serves images via image.tmdb.org. For mock paths we
// route through picsum so the dev experience is reasonable without a token.
const TMDB_IMG = 'https://image.tmdb.org/t/p';

export function posterUrl(path, size = 'w500') {
  if (!path) return placeholderPoster('Untitled');
  if (path.startsWith('/mock/')) {
    const seed = path.replace(/[^a-z0-9]/gi, '');
    return `https://picsum.photos/seed/${seed}/500/750`;
  }
  return `${TMDB_IMG}/${size}${path}`;
}

export function backdropUrl(path, size = 'w1280') {
  if (!path) return null;
  if (path.startsWith('/mock/')) {
    const seed = path.replace(/[^a-z0-9]/gi, '');
    return `https://picsum.photos/seed/${seed}/1280/720`;
  }
  return `${TMDB_IMG}/${size}${path}`;
}

export function placeholderPoster(title) {
  const t = encodeURIComponent((title || '').slice(0, 18));
  // Inline SVG data URI so it always works offline.
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 500 750'>
      <rect width='500' height='750' fill='#1B3F8B'/>
      <rect x='40' y='260' width='420' height='230' rx='8' fill='#FFC72C'/>
      <text x='250' y='395' text-anchor='middle' font-family='Impact, Arial Black, sans-serif'
        font-size='42' fill='#1B3F8B' font-weight='900'>${decodeURIComponent(t)}</text>
    </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export function yearFromReleaseDate(date) {
  return date ? Number(String(date).slice(0, 4)) : null;
}
